import type { ComponentProps, ElementType, ReactNode } from 'react';

import { Tooltip, TooltipContent, TooltipTrigger } from 'design-system';
import { cn } from '@/lib/utils';

export type IconButtonProps = ComponentProps<'button'> & {
  component?: ElementType;
  /** @deprecated Use className for design-system styling. Kept as a no-op during migration. */
  sx?: unknown;
};

export type TooltipIconButtonProps = IconButtonProps & {
  title: string;
  tooltipProps?: Omit<ComponentProps<typeof TooltipContent>, 'children'>;
};

export function IconButton({
  component: Component = 'button',
  className,
  sx: _sx,
  type,
  ...props
}: IconButtonProps) {
  return (
    <Component
      className={cn(
        'inline-flex size-8 items-center justify-center rounded-md text-gx-text-secondary transition-colors hover:bg-gx-response hover:text-gx-text disabled:pointer-events-none disabled:opacity-50',
        className
      )}
      type={Component === 'button' ? (type ?? 'button') : undefined}
      {...props}
    />
  );
}

export function TooltipIconButton({
  title,
  tooltipProps,
  children,
  disabled,
  ...buttonProps
}: TooltipIconButtonProps & { children?: ReactNode }) {
  const button = (
    <IconButton
      aria-label={title}
      disabled={disabled}
      {...buttonProps}
    >
      {children}
    </IconButton>
  );

  return (
    <Tooltip>
      <TooltipTrigger render={disabled ? <span className='inline-flex'>{button}</span> : button} />
      <TooltipContent {...tooltipProps}>{title}</TooltipContent>
    </Tooltip>
  );
}
